'use client'

import { useRef, useState } from 'react'
import { AlertTriangle, Search } from 'lucide-react'
import { Input } from '@/components/marketing/Input'
import { Button } from '@/components/ui/button'
import {
  detectBrandFromDomain,
  type DetectedBrand,
} from '@/lib/detect-brand-client'
import {
  canRunWithoutConfirmation,
  entityDomainKey,
  type AuditEntity,
} from '@/lib/radar/entity-types'
import { RefArrow } from '@/components/reference/tool-live'

export interface CitationSubmission {
  brand: string
  url: string
  category: string
  entity: AuditEntity
}

interface CitationFormProps {
  onSubmit: (submission: CitationSubmission) => void
  /** Invalidate any result/paywall snapshot created from older form values. */
  onDirty?: () => void
  isLoading: boolean
  /**
   * 'reference' renders the reference tool page's own run form
   * (public/pixelmojo-rebuild/pixelmojo-tools/pixelmojo-tool-ai-citation-tracker.html
   * `form.run`), so the page's ported stylesheet draws it exactly like the
   * reference. Same state, detection, entity confirmation and submit.
   */
  variant?: 'default' | 'reference'
}

/** The reference form's example chip (`data-example`). */
const EXAMPLE_URL = 'pixelmojo.io'

function confirmedManualEntity(
  brand: string,
  url: string,
  base: AuditEntity | null
): AuditEntity {
  return {
    domain: entityDomainKey(url),
    brand,
    legalName: base?.legalName ?? null,
    source: 'user_confirmed',
    confidence: base?.confidence ?? 'low',
    requiresConfirmation: false,
    evidence: base?.evidence ?? [],
    conflicts: base?.conflicts ?? [],
    resolverVersion: base?.resolverVersion ?? 'manual',
    resolvedAt: base?.resolvedAt ?? new Date().toISOString(),
    detectedBrand: base?.detectedBrand ?? base?.brand ?? '',
    confirmedByUser: true,
  }
}

export default function CitationForm({
  onSubmit,
  onDirty,
  isLoading,
  variant = 'default',
}: CitationFormProps) {
  const [brand, setBrand] = useState('')
  const [url, setUrl] = useState('')
  const [category, setCategory] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [detecting, setDetecting] = useState(false)
  const [entity, setEntity] = useState<AuditEntity | null>(null)
  const [entityConfirmPending, setEntityConfirmPending] = useState(false)
  const [pendingSubmission, setPendingSubmission] =
    useState<CitationSubmission | null>(null)

  const urlRef = useRef('')
  const brandRef = useRef('')
  const categoryRef = useRef('')
  const entityRef = useRef<AuditEntity | null>(null)
  // Tracks every brand/category value attached to a hostname independently of
  // the entity object. Clearing the brand must not orphan an auto-filled
  // category and let it survive a switch to another site.
  const boundDomainRef = useRef('')
  const detectionGeneration = useRef(0)
  const activeDetection = useRef<{
    domain: string
    promise: Promise<DetectedBrand | null>
  } | null>(null)

  function setBrandValue(value: string) {
    brandRef.current = value
    setBrand(value)
  }

  function setCategoryValue(value: string) {
    categoryRef.current = value
    setCategory(value)
  }

  function setEntityValue(value: AuditEntity | null) {
    entityRef.current = value
    setEntity(value)
  }

  // Auto-fill brand + category from the URL so a domain alone is enough.
  function detectFromUrl(value: string): Promise<DetectedBrand | null> {
    const v = value.trim()
    const requestedDomain = entityDomainKey(v)
    if (!v || !requestedDomain) return Promise.resolve(null)

    if (activeDetection.current?.domain === requestedDomain) {
      return activeDetection.current.promise
    }

    const generation = ++detectionGeneration.current
    setDetecting(true)
    const promise = (async () => {
      const d = await detectBrandFromDomain(v)

      // A response belongs only to the hostname it resolved. A slower request
      // for domain A must never repopulate the form after the user moved to B.
      if (
        generation !== detectionGeneration.current ||
        entityDomainKey(urlRef.current) !== requestedDomain
      ) {
        return null
      }

      if (d) {
        boundDomainRef.current = requestedDomain
        if (d.brand && !brandRef.current.trim()) {
          setBrandValue(d.brand)
          setEntityValue(d)
        }
        if (d.category && !categoryRef.current.trim()) {
          setCategoryValue(d.category)
        }
        setEntityConfirmPending(false)
        setPendingSubmission(null)
      }
      return d
    })().finally(() => {
      if (generation === detectionGeneration.current) {
        setDetecting(false)
        activeDetection.current = null
      }
    })

    activeDetection.current = { domain: requestedDomain, promise }
    return promise
  }

  function validateUrl(value: string): boolean {
    if (!value.trim()) {
      setError('Please enter your website URL.')
      return false
    }
    let test = value.trim()
    if (!test.startsWith('http://') && !test.startsWith('https://')) {
      test = `https://${test}`
    }
    try {
      const parsed = new URL(test)
      if (!parsed.hostname.includes('.')) {
        setError('Please enter a valid domain (e.g. example.com).')
        return false
      }
    } catch {
      setError('Please enter a valid URL.')
      return false
    }
    setError(null)
    return true
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const submittedUrl = urlRef.current.trim()
    if (!validateUrl(submittedUrl)) return
    let b = brandRef.current.trim()
    let c = categoryRef.current.trim()
    let runEntity = entityRef.current
    // Brand and category are auto-detected from the URL; detect on submit if
    // the user left them blank, so the tool runs from a domain alone.
    if (!b || !c || !runEntity) {
      await detectFromUrl(submittedUrl)
      // Inputs deliberately stay editable while detection runs. Read the live
      // refs after awaiting so a human correction always outranks the detector.
      // If the URL changed, this submit belongs to an obsolete form snapshot.
      if (urlRef.current.trim() !== submittedUrl) return
      b = brandRef.current.trim()
      c = categoryRef.current.trim()
      runEntity = entityRef.current
    }
    if (!b) {
      setError('Could not detect your brand. Please enter it.')
      return
    }
    if (b.length > 100) {
      setError('Please keep the brand name under 100 characters.')
      return
    }
    if (!c) {
      setError('Could not detect your category. Please enter it.')
      return
    }
    if (c.length > 100) {
      setError('Please keep the category under 100 characters.')
      return
    }

    // A manually entered/corrected brand is itself a human confirmation. Keep
    // the original detected value for forensics while making the edit
    // authoritative for this domain.
    const unboundManualEntity =
      runEntity?.source === 'user_confirmed' &&
      !entityDomainKey(runEntity.domain)
    if (!runEntity || runEntity.brand.trim() !== b || unboundManualEntity) {
      runEntity = confirmedManualEntity(b, submittedUrl, runEntity)
      setEntityValue(runEntity)
    }
    boundDomainRef.current = entityDomainKey(submittedUrl)

    const submission = {
      brand: b,
      url: submittedUrl,
      category: c,
      entity: runEntity,
    }

    if (
      !canRunWithoutConfirmation(runEntity, submission.url, submission.brand)
    ) {
      setEntityConfirmPending(true)
      setPendingSubmission(submission)
      return
    }

    setEntityConfirmPending(false)
    setPendingSubmission(null)
    onSubmit(submission)
  }

  function confirmEntity() {
    if (!entity || !pendingSubmission) return
    const confirmed: AuditEntity = {
      ...entity,
      domain: entityDomainKey(pendingSubmission.url),
      brand: pendingSubmission.brand,
      requiresConfirmation: false,
      confirmedByUser: true,
    }
    if (
      !canRunWithoutConfirmation(
        confirmed,
        pendingSubmission.url,
        pendingSubmission.brand
      )
    ) {
      setError('Please confirm a company for this website before running.')
      return
    }

    setEntityValue(confirmed)
    boundDomainRef.current = entityDomainKey(pendingSubmission.url)
    setEntityConfirmPending(false)
    setPendingSubmission(null)
    onSubmit({ ...pendingSubmission, entity: confirmed })
  }

  // Field edits. Shared by both renders, so every variant invalidates the
  // same state: a new hostname drops the brand/category bound to the old one,
  // any edit cancels a pending confirmation and dirties the page snapshot.
  function changeUrl(nextUrl: string) {
    const boundDomain = boundDomainRef.current
    const leavesBoundDomain =
      !!boundDomain && entityDomainKey(nextUrl) !== boundDomain
    urlRef.current = nextUrl
    setUrl(nextUrl)
    detectionGeneration.current += 1
    activeDetection.current = null
    setDetecting(false)
    setEntityConfirmPending(false)
    setPendingSubmission(null)
    if (leavesBoundDomain) {
      setBrandValue('')
      setCategoryValue('')
      setEntityValue(null)
      boundDomainRef.current = ''
    }
    if (error) setError(null)
    onDirty?.()
  }

  function changeBrand(value: string) {
    setBrandValue(value)
    const currentDomain = entityDomainKey(urlRef.current)
    if (currentDomain) boundDomainRef.current = currentDomain
    setEntityConfirmPending(false)
    setPendingSubmission(null)
    setEntityValue(
      value.trim()
        ? confirmedManualEntity(value.trim(), urlRef.current, entityRef.current)
        : null
    )
    if (error) setError(null)
    onDirty?.()
  }

  function changeCategory(value: string) {
    setCategoryValue(value)
    const currentDomain = entityDomainKey(urlRef.current)
    if (currentDomain) boundDomainRef.current = currentDomain
    setEntityConfirmPending(false)
    setPendingSubmission(null)
    if (error) setError(null)
    onDirty?.()
  }

  const conflictCopy = entity?.conflicts.length
    ? `This site gives conflicting name signals: ${entity.conflicts[0].values[0]} vs ${entity.conflicts[0].values[1]}.`
    : 'We could not confirm this company name from the site with enough certainty.'

  if (variant === 'reference') {
    const busy = isLoading || detecting
    return (
      <form className='run' noValidate onSubmit={handleSubmit}>
        <label className='run__field'>
          <span>Website URL</span>
          <input
            type='text'
            name='f0'
            value={url}
            onChange={e => changeUrl(e.target.value)}
            onBlur={() => void detectFromUrl(urlRef.current)}
            placeholder='Website URL (e.g. shopify.com)'
            autoComplete='off'
            inputMode='url'
            disabled={isLoading}
          />
        </label>
        <div className='run__row'>
          <label className='run__field'>
            <span>
              Brand
              {detecting ? <i>Detecting…</i> : null}
            </span>
            <input
              type='text'
              name='f1'
              maxLength={100}
              value={brand}
              onChange={e => changeBrand(e.target.value)}
              placeholder={
                detecting
                  ? 'Detecting brand...'
                  : 'Brand (auto-detected from URL)'
              }
              autoComplete='off'
              disabled={isLoading}
            />
          </label>
          <label className='run__field'>
            <span>Category</span>
            <input
              type='text'
              name='f2'
              maxLength={100}
              value={category}
              onChange={e => changeCategory(e.target.value)}
              placeholder='Category (auto-detected, edit if needed)'
              autoComplete='off'
              disabled={isLoading}
            />
          </label>
        </div>
        <div className='run__actions'>
          <button
            className='btn btn--accent'
            type='submit'
            disabled={busy}
            aria-busy={busy || undefined}
          >
            {isLoading
              ? 'Checking…'
              : detecting
                ? 'Detecting…'
                : 'Check Citations'}
            <RefArrow />
          </button>
          <button
            type='button'
            className='btn btn--ghost btn--sm'
            data-example={EXAMPLE_URL}
            disabled={isLoading}
            onClick={() => {
              changeUrl(EXAMPLE_URL)
              void detectFromUrl(EXAMPLE_URL)
            }}
          >
            Try {EXAMPLE_URL}
          </button>
        </div>
        {entityConfirmPending && pendingSubmission && (
          <div role='alert' className='unlock run__confirm'>
            <div>
              <b>Confirm the company before running</b>
              <span>
                {conflictCopy} Auditing the wrong company produces an invalid
                report.
              </span>
              <span>
                We detected <strong>{pendingSubmission.brand}</strong>. Or
                correct the brand field, then run again.
              </span>
            </div>
            <button
              type='button'
              className='btn btn--accent btn--sm'
              onClick={confirmEntity}
            >
              Yes, that&apos;s the company
            </button>
          </div>
        )}
        <p className='run__note'>
          <i />
          <span>
            Pay a one-time $5 to run this check. Your report opens right here.
          </span>
        </p>
        {error && (
          <p className='run__note run__error' role='alert'>
            <i />
            {error}
          </p>
        )}
      </form>
    )
  }

  return (
    <form onSubmit={handleSubmit} className='w-full max-w-2xl mx-auto'>
      <div className='flex flex-col gap-3'>
        <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
          <div className='relative'>
            <Input
              type='text'
              aria-label='Website URL'
              value={url}
              onChange={e => changeUrl(e.target.value)}
              onBlur={() => void detectFromUrl(urlRef.current)}
              placeholder='Website URL (e.g. shopify.com)'
              className='h-12 min-h-0 rounded-full py-0 text-base'
              disabled={isLoading}
            />
          </div>
          <div className='relative'>
            <Input
              type='text'
              aria-label='Brand name'
              maxLength={100}
              value={brand}
              onChange={e => changeBrand(e.target.value)}
              placeholder={
                detecting
                  ? 'Detecting brand...'
                  : 'Brand (auto-detected from URL)'
              }
              className='h-12 min-h-0 rounded-full py-0 text-base'
              disabled={isLoading}
            />
          </div>
        </div>
        <div className='flex flex-col sm:flex-row gap-3'>
          <div className='relative flex-1'>
            <Search
              size={18}
              className='absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground'
            />
            <Input
              type='text'
              aria-label='Category'
              maxLength={100}
              value={category}
              onChange={e => changeCategory(e.target.value)}
              placeholder='Category (auto-detected, edit if needed)'
              className='h-12 min-h-0 rounded-full py-0 pl-11 pr-4 text-base'
              disabled={isLoading}
            />
          </div>
          <Button
            type='submit'
            variant='default'
            size='lg'
            loading={isLoading || detecting}
            disabled={isLoading || detecting}
            className='h-12 px-8'
          >
            Check Citations
          </Button>
        </div>
      </div>
      {entityConfirmPending && pendingSubmission && (
        <div
          role='alert'
          className='mt-4 border border-hairline-strong bg-muted/40 p-4 text-left flex flex-col gap-3'
        >
          <div className='flex items-center gap-2'>
            <AlertTriangle size={14} className='shrink-0 text-warning' />
            <span className='text-sm font-semibold text-foreground'>
              Confirm the company before running
            </span>
          </div>
          <p className='text-xs text-muted-foreground'>
            {conflictCopy} Auditing the wrong company produces an invalid
            report.
          </p>
          <div>
            <span className='block text-[11px] font-bold uppercase tracking-widest text-muted-foreground'>
              We detected
            </span>
            <span className='text-sm font-semibold text-foreground'>
              {pendingSubmission.brand}
            </span>
          </div>
          <div className='flex flex-wrap items-center gap-2'>
            <Button type='button' size='sm' onClick={confirmEntity}>
              Yes, that&apos;s the company
            </Button>
            <span className='text-xs text-muted-foreground'>
              Or correct the brand field, then run again.
            </span>
          </div>
        </div>
      )}
      {error && (
        <p className='text-sm text-[#F90B8A] mt-2 text-center'>{error}</p>
      )}
    </form>
  )
}
